import {
  CostCenterValidators,
  UserValidators,
  CloudUtils,
  AuthorizedDeviceValidators,
} from '@portal/portal-core';
import { get } from 'lodash-es';

import { wrapJoiSchema, composeValidators } from '..';

// ------------------------------------------------------------------------------------------------
// Simple
// ------------------------------------------------------------------------------------------------

export const validateID = {
  validator: CloudUtils.isValidUUID,
  getValidationMessage: () => 'Invalid ID format.',
};

export const validateNotEmpty = {
  validator: (val: unknown) => !!val,
  getValidationMessage: () => 'Value cannot be empty.',
};

export const validateFirstName = {
  validator: wrapJoiSchema(UserValidators.FirstnameSchema),
  getValidationMessage: () => 'First name is invalid.',
};

export const validateLastName = {
  validator: wrapJoiSchema(UserValidators.LastnameSchema),
  getValidationMessage: () => 'Last name is invalid.',
};

export const validateEmail = {
  validator: wrapJoiSchema(UserValidators.EmailValidationSchema),
  getValidationMessage: () => 'Email format is invalid.',
};

export const validateEmailSuffix = {
  validator: wrapJoiSchema(CostCenterValidators.EmailDomainValidationSchema),
  getValidationMessage: () => 'Email domain is invalid.',
};

export const validateCostCenterName = {
  validator: wrapJoiSchema(CostCenterValidators.NameValidationSchema),
  getValidationMessage: () => 'Cost center name is invalid.',
};

export const validateUSZipCode = {
  validator: (val: string) => /(^\d{5}$)|(^\d{5}-\d{4}$)/.test(val),
  getValidationMessage: () => 'Value is not a valid US Zip Code.',
};

export const validateSeatCount = {
  validator: (val: string) => parseInt(val, 10) <= CostCenterValidators.MAX_COST_CENTER_USERS,
  getValidationMessage: () => 'Value is not a valid seat count.',
};

export const validateAuthorizedDeviceFingerprint = {
  validator: wrapJoiSchema(AuthorizedDeviceValidators.FingerprintSchema),
  getValidationMessage: () => 'Fingerprint must be 16 characters including 1-9 or a-f.',
};

// ------------------------------------------------------------------------------------------------
// Configured
// ------------------------------------------------------------------------------------------------
// These validators are *built* by invoking the higher-order wrapper with information to check
// against

export const validateMaxLength = (maxLen: number) => ({
  validator: (val: string) => val.length <= maxLen,
  getValidationMessage: () => `Max length ${maxLen}.`,
});

export const validateMinLength = (minLen: number) => ({
  validator: (val: string) => val.length >= minLen,
  getValidationMessage: () => `Min length ${minLen}.`,
});

export const validateNoDuplicates = (existing: unknown[] = [], pathToComparisonField?: string) => ({
  validator: (val: unknown) =>
    existing.every((item) => {
      const comparisonField = pathToComparisonField ? get(item, pathToComparisonField) : item;
      return comparisonField !== val;
    }),
  getValidationMessage: () => 'Duplicate entry.',
});

// ------------------------------------------------------------------------------------------------
// Composed
// ------------------------------------------------------------------------------------------------

export const validateAddress = composeValidators(
  // nothing notable re: address validation in cloud-core, but can add additional
  // validators here
  validateNotEmpty,
);

export const validateDiskSpace = composeValidators(
  // nothing notable re: disk space in cloud-core, but can add additional
  // validators here
  validateNotEmpty,
);

export const validateDownloadSpace = composeValidators(
  // nothing notable re: download space in cloud-core, but can add additional
  // validators here
  validateNotEmpty,
);

export const validatePhone = composeValidators(
  // nothing notable re: phone in cloud-core, but can add additional
  // validators here
  validateNotEmpty,
);
